import StackKit from "@/shared/components/kit/stack";
import TypoGraphyKit from "@/shared/components/kit/typoGraphy";
import React, { SetStateAction, useEffect, useState } from "react";
import ModeEditOutlinedIcon from "@mui/icons-material/ModeEditOutlined";
import { Grid, TextField } from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import { postEditClubName } from "@/api/merchant/request.post";
import { AxiosError } from "axios";
import { toast } from "react-toastify";
import CustomButton from "@/shared/components/kit/button";
import { AddImg } from "@/shared/components/common/icons";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import CloseIcon from "@mui/icons-material/Close";
import { fileToBase64 } from "@/hooks/base64";
import { CircleLoader, DotLoader, PulseLoader } from "react-spinners";

type HeaderContentProps = {
  data: any;
  id: string | undefined;
  setEnabled: React.Dispatch<SetStateAction<boolean>>;
};

const HeaderContent = ({ data, id, setEnabled }: HeaderContentProps) => {
  const [isEditBle, setIsEditBle] = useState(false);
  const [valueField, setValueField] = useState("");
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string>("");

  useEffect(() => {
    setValueField(data?.club?.title);
    setImagePreview(data?.club?.image || "");
  }, [data?.club?.title, data?.club?.image]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setValueField(e.target.value);
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.type.startsWith("image/")) {
        toast.error("لطفاً یک فایل تصویری انتخاب کنید");
        return;
      }

      if (file.size > 5 * 1024 * 1024) {
        toast.error("حجم فایل نباید بیشتر از 5 مگابایت باشد");
        return;
      }

      setSelectedImage(file);

      const previewUrl = URL.createObjectURL(file);
      setImagePreview(previewUrl);
    }
  };

  const { mutate: fetchEditClubName, isPending } = useMutation({
    mutationFn: postEditClubName,
    onSuccess: () => {
      setIsEditBle(false);
      setSelectedImage(null);
      toast.success("با موفقیت تغییر کرد");
      setEnabled(true);
    },
    onError: (error: AxiosError<{ message: string }>) => {
      toast.error(error?.response?.data?.message || "خطایی رخ داده است");
    },
  });


  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    try {
      let imageBase64 = "";

      if (selectedImage) {
        imageBase64 = await fileToBase64(selectedImage);
      }

      const requestBody: any = { id: id, title: valueField, status: "1" };

      if (selectedImage) {
        requestBody.image = imageBase64;
      } else if (!imagePreview && data?.club?.image) {
        requestBody.image = "";
      }

      fetchEditClubName(requestBody);

    } catch (error) {
      toast.error("خطا در تبدیل تصویر");
      console.error(error);
    }
  };

  const handleCancel = () => {
    setIsEditBle(false);
    setSelectedImage(null);
    setValueField(data?.club?.title);
    setImagePreview(data?.club?.image || "");
  };

  return (
    <StackKit sx={{ width: "100%" }}>
      <Grid container spacing={2} sx={{ width: "100%", alignItems: "center" }}>
        <Grid size={{ xs: 12, md: 9 }}>
          <StackKit sx={{ width: "100%" }} alignItems="start" gap={2}>
            {isEditBle ? (
              <form onSubmit={handleSubmit} style={{ width: "100%" }}>
                <StackKit direction={{ xs: "column", sm: "row" }} gap={2} sx={{ width: "100%" }}>
                  <TextField value={valueField} onChange={handleChange} fullWidth label="نام کلاب" required />
                  <CustomButton type="submit" sx={{ minWidth: 100 }} disabled={isPending}>
                    {isPending ?
                      <PulseLoader size={10} color="#fff" />
                      :
                      <TypoGraphyKit variant="body1">
                        ذخیره
                      </TypoGraphyKit>
                    }
                  </CustomButton>
                  <CustomButton type="button" sx={{ minWidth: 100 }} onClick={handleCancel} disabled={isPending}>
                    <TypoGraphyKit variant="body1">انصراف</TypoGraphyKit>
                  </CustomButton>
                </StackKit>
              </form>
            ) : (
              <StackKit direction={{ xs: "column", sm: "row" }} gap={2} alignItems={{ xs: "flex-start", sm: "center" }}>
                <TypoGraphyKit variant="body1">{data?.club?.title}</TypoGraphyKit>
                <StackKit sx={{ width: "24px", height: "24px", borderRadius: "8px", bgcolor: "#2C2C2C", cursor: "pointer", }} justifyContent="center" alignItems="center"
                  onClick={() => setIsEditBle(true)}
                >
                  <ModeEditOutlinedIcon sx={{ width: "18px", color: "white" }} />
                </StackKit>
              </StackKit>
            )}
            <TypoGraphyKit variant="body1">پذیرنده گرامی</TypoGraphyKit>
            <TypoGraphyKit variant="body1" sx={{ textAlign: "right" }}>
              در این بخش شما میتوانید کاربران ثبت شده در این سازمان را مشاهده
              کنید. توجه داشته باشد که اطلاعات کاربران ثبت شده در این بخش فقط
              برای استفاده در فروشگاه شما میباشد. شما میتوانید به صورت کلی یا
              انفرادی اعتبارات و موجودی کاربران را افزایش یا کاهش دهید
            </TypoGraphyKit>
          </StackKit>
        </Grid>

        <Grid size={{ xs: 12, md: 3 }}>
          <StackKit justifyContent="center" alignItems="center"
            sx={{ width: { xs: "100%", sm: 163 }, height: { xs: "200px", sm: 187 }, bgcolor: "#A1A1A1", borderRadius: "5px", marginTop: { xs: 2, md: 0 }, position: "relative", overflow: "hidden", }}
          >
            {isEditBle && (
              <StackKit sx={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0, bgcolor: "rgba(0, 0, 0, 0.7)", zIndex: 2, justifyContent: "center", alignItems: "center", }}>
                <StackKit direction="column" gap={2} alignItems="center">
                  <input type="file" accept="image/*" id="club-image-upload" style={{ display: "none" }} onChange={handleImageChange} />
                  <label htmlFor="club-image-upload">
                    <CustomButton component="span" startIcon={<ImageOutlinedIcon />} sx={{ color: "white" }} disabled={isPending}>
                      انتخاب تصویر
                    </CustomButton>
                  </label>
                </StackKit>
              </StackKit>
            )}

            {imagePreview ? (
              <img src={imagePreview} alt={data?.club?.title} style={{ width: "100%", height: "100%", objectFit: "cover", position: "absolute", top: 0, left: 0, }} />
            ) : data?.club?.image ? (
              <img src={data.club.image} alt={data?.club?.title} style={{ width: "100%", height: "100%", objectFit: "cover", position: "absolute", top: 0, left: 0, }} />
            ) : (
              <AddImg />
            )}
          </StackKit>
        </Grid>
      </Grid>
    </StackKit>
  );
};

export default HeaderContent;